import { PORTABLE_BLOB_BYTES_MAX } from './constants.js';
import { StorageError } from './errors.js';
import { assertBlobRef } from './validation.js';
const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function cryptoSubtle() {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle)
        throw new StorageError('unsupported', { detail: 'Web Crypto SHA-256 is required for blobs' });
    return subtle;
}
export async function sha256(input) {
    const digest = new Uint8Array(await cryptoSubtle().digest('SHA-256', input));
    let hex = '';
    for (const byte of digest)
        hex += byte.toString(16).padStart(2, '0');
    return `sha256:${hex}`;
}
export async function createBlobRef(input, mime, clock) {
    if (!(input instanceof Blob))
        throw new StorageError('invalid-data', { detail: 'Blob input is required' });
    if (input.size > PORTABLE_BLOB_BYTES_MAX)
        throw new StorageError('limit-exceeded');
    if (mime.length === 0 || mime.length > 255)
        throw new StorageError('invalid-data', { detail: 'Blob mime is invalid' });
    const hash = await sha256(await input.arrayBuffer());
    const ref = {
        id: `blob_${hash.slice('sha256:'.length)}`,
        mime,
        size: input.size,
        hash,
        createdAt: clock.now(),
    };
    assertBlobRef(ref);
    return ref;
}
export async function assertBlobIntegrity(blob, ref) {
    assertBlobRef(ref);
    if (blob.size !== ref.size)
        throw new StorageError('integrity-failed', { detail: `Blob ${ref.id} size mismatch` });
    const hash = await sha256(await blob.arrayBuffer());
    if (hash !== ref.hash)
        throw new StorageError('integrity-failed', { detail: `Blob ${ref.id} hash mismatch` });
}
export function encodeBase64(bytes) {
    if (typeof globalThis.btoa !== 'function') {
        throw new StorageError('unsupported', { detail: 'Base64 encoding is unavailable' });
    }
    const chunks = [];
    const chunkBytes = 24_576; // Divisible by three, so only the final chunk has padding.
    for (let offset = 0; offset < bytes.length; offset += chunkBytes) {
        const end = Math.min(offset + chunkBytes, bytes.length);
        let binary = '';
        for (let index = offset; index < end; index += 1)
            binary += String.fromCharCode(bytes[index] ?? 0);
        chunks.push(globalThis.btoa(binary));
    }
    return chunks.join('');
}
export function decodeBase64(value) {
    if (value.length % 4 !== 0)
        throw new StorageError('invalid-data', { detail: 'Invalid base64 length' });
    const padding = value.endsWith('==') ? 2 : value.endsWith('=') ? 1 : 0;
    const output = new Uint8Array(value.length / 4 * 3 - padding);
    let outputIndex = 0;
    for (let index = 0; index < value.length; index += 4) {
        const a = BASE64_ALPHABET.indexOf(value[index] ?? '');
        const b = BASE64_ALPHABET.indexOf(value[index + 1] ?? '');
        const c = value[index + 2] === '=' ? 0 : BASE64_ALPHABET.indexOf(value[index + 2] ?? '');
        const d = value[index + 3] === '=' ? 0 : BASE64_ALPHABET.indexOf(value[index + 3] ?? '');
        if (a < 0 || b < 0 || c < 0 || d < 0)
            throw new StorageError('invalid-data', { detail: 'Invalid base64 data' });
        const combined = (a << 18) | (b << 12) | (c << 6) | d;
        if (outputIndex < output.length)
            output[outputIndex++] = (combined >>> 16) & 255;
        if (outputIndex < output.length)
            output[outputIndex++] = (combined >>> 8) & 255;
        if (outputIndex < output.length)
            output[outputIndex++] = combined & 255;
    }
    return output;
}
export function approximateJsonBytes(value) {
    return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}
//# sourceMappingURL=bytes.js.map